import {
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';

@Component({
  selector: 'app-file-input',
  templateUrl: './file-input.component.html',
  styleUrls: ['./file-input.component.scss']
})
export class FileInputComponent implements OnChanges {
  @Input() extensions: string[] = [];
  /** allows to upload multiple files */
  @Input() multiple = false;
  /** size limit of single file in megabytes, 0 means no limit */
  @Input() fileSizeMB = 0;
  /** accepted MIME types, empty means no MIME validation */
  @Input() mimeTypes: string[] = [];

  /** used to set custom icon in place of standard mat-icon attachment */
  @ContentChild('icon') customIconTmp?: TemplateRef<any>;
  /** used to add custom footer */
  @ContentChild('footer') customFooterTmp?: TemplateRef<any>;
  /** used to set completely custom visuals - icon and footer templates will be ignored */
  @ContentChild('content') customContentTmp?: TemplateRef<any>;

  /** reference to internal input component */
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  @Output() filesAdded: EventEmitter<File[]> = new EventEmitter<File[]>();

  // html
  public extensionsHr = '';
  public error = '';

  // css
  public primary = true;
  public drag = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.extensions) {
      this.extensionsHr = changes.extensions.currentValue.map((x: string) => x.replace('.', '').toUpperCase()).join(', ');
    }
  }

  public onFileDrop(e: DragEvent): void {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer?.files;
    if (files) this.handleUpload(files);
  }

  public onFileSelected(e: Event): void {
    const files = (e.target as HTMLInputElement | null)?.files;
    if (files) this.handleUpload(files);
  }

  private handleUpload(files: FileList): void {
    const selectedFiles = Array.from(files);
    if (!this.multiple && selectedFiles.length > 1) {
      this.error = 'error.oneFileAllowed';
      return;
    }
    if (!this.validateSize(selectedFiles)) {
      this.error = 'error.maxFileSize';
      return;
    }
    if (!this.validateExtensions(selectedFiles) || !this.validateMimeTypes(selectedFiles)) {
      this.error = 'error.fileType';
      return;
    }
    this.error = '';
    this.filesAdded.emit(selectedFiles);
  }

  private validateSize(files: File[]): boolean {
    return this.fileSizeMB <= 0 || files.every((file) => file.size <= this.fileSizeMB * 1_000_000);
  }

  private validateExtensions(files: File[]): boolean {
    if (!this.extensions.length) return true;

    const extensionPattern = /\.([0-9a-z]+)(?=[?#])|(\.)(?:[\w]+)$/i;
    return files.every((file) => {
      const extension = file.name.toLowerCase().match(extensionPattern)?.[0];
      return !!extension && this.extensions.includes(extension);
    });
  }

  private validateMimeTypes(files: File[]): boolean {
    if (!this.mimeTypes.length) return true;

    return files.every((file) => this.mimeTypes.includes(file.type.toLowerCase()));
  }
}
